import axios from "axios";

// Support mode (fase 4): Breefer admins only see data per person (scores, results per participant) with a reason.
// The reason is sent as X-Support-Reason for 30 minutes and every look-up is written to the audit log.
// When the API answers 428 (reason required), the SupportModeDialog asks for one and the call is repeated.

const KEY = "supportMode";
const DURATION_MS = 30 * 60 * 1000;
export const SUPPORT_EVENT = "breefer:support-mode";

interface SupportMode {
  reason: string;
  until: number;
}

export const getSupportMode = (): SupportMode | null => {
  try {
    const mode = JSON.parse(sessionStorage.getItem(KEY) || "null") as SupportMode | null;
    return mode && mode.until > Date.now() ? mode : null;
  } catch {
    return null;
  }
};

export const startSupportMode = (reason: string) => {
  sessionStorage.setItem(KEY, JSON.stringify({ reason, until: Date.now() + DURATION_MS }));
  window.dispatchEvent(new Event(SUPPORT_EVENT));
};

export const endSupportMode = () => {
  sessionStorage.removeItem(KEY);
  window.dispatchEvent(new Event(SUPPORT_EVENT));
};

// The dialog registers itself here; a 428 waits for the admin's answer (null = cancelled)
let askForReason: (() => Promise<string | null>) | null = null;
export const registerReasonPrompt = (prompt: (() => Promise<string | null>) | null) => {
  askForReason = prompt;
};
export const requestSupportMode = async () => {
  const reason = askForReason ? await askForReason() : null;
  if (reason) startSupportMode(reason);
  return Boolean(reason);
};

export const startSupportModeInterceptors = () => {
  axios.interceptors.request.use((config) => {
    const mode = getSupportMode();
    if (mode && String(config.url || "").includes("/v1/")) {
      config.headers = config.headers || {};
      (config.headers as any)["X-Support-Reason"] = mode.reason;
    }
    return config;
  });
  axios.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    if (error?.response?.status !== 428 || !config || config._supportRetried) throw error;
    if (!(await requestSupportMode())) throw error;
    config._supportRetried = true;
    config.headers = { ...config.headers, "X-Support-Reason": getSupportMode()?.reason };
    return axios.request(config);
  });
};
